import { useCallback, useEffect, useRef, useState } from 'react';

import { toast } from 'sonner';

interface UseCopyToClipboardOptions {
  /** How long the `copied` flag stays true after a successful copy. */
  resetMs?: number;
  /** Toast shown when the clipboard write fails (e.g. denied permission, insecure context). */
  errorMessage?: string;
}

interface UseCopyToClipboardResult {
  copied: boolean;
  copy: () => Promise<void>;
}

/**
 * Copies `text` to the clipboard and exposes a transient `copied` flag for inline
 * "Copied!" feedback. Owns the reset timer and its cleanup so consumers stay effect-free.
 */
export function useCopyToClipboard(
  text: string,
  { resetMs = 3000, errorMessage = 'Could not copy to clipboard' }: UseCopyToClipboardOptions = {},
): UseCopyToClipboardResult {
  const [copied, setCopied] = useState(false);
  const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    },
    [],
  );

  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);

      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
      resetTimerRef.current = setTimeout(() => setCopied(false), resetMs);
    } catch {
      toast.error(errorMessage);
    }
  }, [text, resetMs, errorMessage]);

  return { copied, copy };
}
